import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { theme, media } from '../styles/theme';
import { RootState, AppDispatch } from '../redux/store';
import { clearBuylist, removeFromBuylist, updateBuylistQuantity, setPaymentMethod } from '../redux/buylistSlice';
import { submitBuylistOrder, submitGuestBuylistOrder, getBuylistImageUrl } from '../services/buylistService';
import { BuylistCartItem, BuylistPaymentMethod, BuylistOrderItem, BuylistOrder } from '../types';
import BuylistLayout from '../components/buylist/BuylistLayout';
import { FoilBadge } from '../styles/components';
import { trackInitiateCheckout, trackPurchase } from '../utils/metaPixel';

const PageContainer = styled.div`
  max-width: 1400px;
  margin: 2rem auto;
  padding: 0 1rem;

  ${media.mobile} {
    padding: 0 0.75rem;
    margin: 1rem auto;
  }
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  color: ${theme.colors.text};

  ${media.mobile} {
    font-size: 1.5rem;
    margin-bottom: 1rem;
  }
`;

const CheckoutGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }

  ${media.mobile} {
    gap: 1rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: ${theme.colors.text};
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const OrderTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 2rem;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  overflow: hidden;
  box-shadow: ${theme.shadows.card};

  ${media.tablet} {
    display: none;
  }
`;

const TableHead = styled.thead`
  background-color: ${theme.colors.background};

  th {
    padding: 1rem;
    text-align: left;
    font-weight: 600;
    border-bottom: 2px solid ${theme.colors.border};
  }
`;

const TableBody = styled.tbody`
  tr {
    border-bottom: 1px solid ${theme.colors.border};

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background-color: ${theme.colors.background};
    }
  }

  td {
    padding: 1rem;
    vertical-align: middle;
  }
`;

const ItemImage = styled.div<{ $imageUrl: string }>`
  width: 60px;
  height: 60px;
  background-image: url(${props => props.$imageUrl});
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  background-color: ${theme.colors.background};
  border-radius: ${theme.borderRadius.sm};
`;

const ItemName = styled.h3`
  font-size: 1rem;
  margin: 0 0 0.25rem 0;
  color: ${theme.colors.text};
`;

const ItemInfo = styled.p`
  font-size: 0.85rem;
  color: ${theme.colors.muted};
  margin: 0 0 0.25rem 0;
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const QuantityButton = styled.button`
  width: 28px;
  height: 28px;
  border: 1px solid ${theme.colors.border};
  background-color: ${theme.colors.background};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  padding: 0;

  &:hover {
    background-color: ${theme.colors.border};
  }

  &:disabled {
    background-color: ${theme.colors.background};
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const QuantityDisplay = styled.span`
  padding: 0 0.75rem;
  border-top: 1px solid ${theme.colors.border};
  border-bottom: 1px solid ${theme.colors.border};
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.danger};
  cursor: pointer;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: ${theme.borderRadius.sm};

  &:hover {
    background-color: rgba(231, 76, 60, 0.1);
  }
`;

const Price = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${theme.colors.success};
`;

const OrderSummary = styled.div`
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  padding: 1.5rem;
  background-color: ${theme.colors.white};
  box-shadow: ${theme.shadows.card};
  height: fit-content;

  ${media.mobile} {
    padding: 1rem;
  }
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 0;
  border-bottom: 1px solid ${theme.colors.border};

  &:last-child {
    border-bottom: none;
    font-weight: 700;
    font-size: 1.1rem;
    padding-top: 1rem;
  }
`;

const PaymentMethodContainer = styled.div`
  margin-top: 1.5rem;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  padding: 1.5rem;
  background-color: ${theme.colors.white};
  box-shadow: ${theme.shadows.card};

  ${media.mobile} {
    padding: 1rem;
  }
`;

const PaymentMethodCard = styled.div`
  padding: 1rem;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  margin-bottom: 1rem;
  background-color: ${theme.colors.background};
`;

// Inline selector so the customer can switch payout method (e.g. back to bank
// transfer) without leaving checkout and losing their entered details.
const PaymentMethodSelector = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const PaymentOption = styled.label`
  display: flex;
  align-items: center;
  padding: 0.75rem;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  transition: border-color 0.3s, background-color 0.3s;

  input {
    margin-right: 0.75rem;
  }

  &:hover {
    background-color: ${theme.colors.background};
  }

  &.selected {
    border-color: ${theme.colors.primary};
    background-color: rgba(57, 132, 198, 0.1);
  }
`;

const PaymentInfo = styled.div`
  margin-top: 1rem;
`;

const WireTransferInfo = styled.div`
  margin-top: 1rem;
  background-color: ${theme.colors.background};
  padding: 1rem;
  border-radius: ${theme.borderRadius.md};
  border: 1px dashed ${theme.colors.primary};
`;

const ConfirmButton = styled.button`
  width: 100%;
  padding: 1rem;
  margin-top: 1.5rem;
  background-color: ${theme.colors.success};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.sm};
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s;

  &:hover {
    filter: brightness(0.85);
  }

  &:disabled {
    background-color: ${theme.colors.gray.medium};
    cursor: not-allowed;
    filter: none;
  }
`;

const TermsCheckboxContainer = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-top: 1rem;
  font-size: 0.9rem;
  color: ${theme.colors.muted};
  cursor: pointer;

  input[type="checkbox"] {
    margin-top: 3px;
    cursor: pointer;
    accent-color: ${theme.colors.success};
  }

  a {
    color: ${theme.colors.primary};
    text-decoration: underline;

    &:hover {
      filter: brightness(0.85);
    }
  }
`;

const ErrorMessage = styled.div`
  color: ${theme.colors.danger};
  padding: 1rem;
  background-color: rgba(231, 76, 60, 0.1);
  border-radius: ${theme.borderRadius.sm};
  margin-bottom: 1rem;
`;

const SuccessMessage = styled.div`
  color: ${theme.colors.success};
  padding: 1rem;
  background-color: rgba(45, 184, 107, 0.15);
  border-radius: ${theme.borderRadius.sm};
  margin-bottom: 1rem;
  text-align: center;
`;

const DroppedItemsWarning = styled.div`
  color: ${theme.colors.text};
  padding: 1rem;
  background-color: rgba(243, 156, 18, 0.12);
  border: 1px solid rgba(243, 156, 18, 0.5);
  border-radius: ${theme.borderRadius.sm};
  margin: 1rem 0;
  text-align: left;

  h3 {
    margin: 0 0 0.5rem 0;
    font-size: 1rem;
  }

  ul {
    margin: 0.5rem 0 0 0;
    padding-left: 1.25rem;
  }
`;

const InputField = styled.input`
  width: 100%;
  padding: 0.75rem;
  margin-top: 0.5rem;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }

  &:disabled {
    background-color: ${theme.colors.background};
    cursor: not-allowed;
  }
`;

const InputLabel = styled.label`
  font-weight: 600;
  margin-top: 1rem;
  display: block;
`;

const ContactSection = styled.div`
  margin-bottom: 2rem;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  padding: 1.5rem;
  background-color: ${theme.colors.white};
  box-shadow: ${theme.shadows.card};

  ${media.mobile} {
    padding: 1rem;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FieldError = styled.p`
  color: ${theme.colors.danger};
  font-size: 0.85rem;
  margin-top: 0.25rem;
`;

// Mobile card layout for order items (shown on tablet and below)
const MobileCardList = styled.div`
  display: none;

  ${media.tablet} {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-bottom: 2rem;
  }
`;

const MobileCard = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: 0.75rem;
  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  box-shadow: ${theme.shadows.card};
`;

const MobileCardImage = styled.div<{ $imageUrl: string }>`
  width: 60px;
  height: 80px;
  flex-shrink: 0;
  background-image: url(${props => props.$imageUrl});
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  background-color: ${theme.colors.background};
  border-radius: ${theme.borderRadius.sm};
`;

const MobileCardContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const MobileCardRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.5rem;
`;

interface ContactErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  gateadresse?: string;
  postnummer?: string;
  poststed?: string;
}

const BuylistCheckoutPage: React.FC = () => {
  const { items, totalQuantity, totalAmount, paymentMethod } = useSelector((state: RootState) => state.buylist);
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [orderNumber, setOrderNumber] = useState<string | null>(null);
  const [droppedItems, setDroppedItems] = useState<string[]>([]);
  const [accountToPayTo, setAccountToPayTo] = useState<string>('');
  const [accountError, setAccountError] = useState<string | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const accountNumberRef = useRef<HTMLInputElement>(null);

  // Contact details
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [gateadresse, setGateadresse] = useState('');
  const [postnummer, setPostnummer] = useState('');
  const [poststed, setPoststed] = useState('');
  const [contactErrors, setContactErrors] = useState<ContactErrors>({});

  // Track InitiateCheckout on mount
  useEffect(() => {
    if (items.length > 0) {
      trackInitiateCheckout(totalQuantity, totalAmount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-fill from profile when logged in
  useEffect(() => {
    if (isAuthenticated && user) {
      if (user.fornavn) setFirstName(user.fornavn);
      if (user.etternavn) setLastName(user.etternavn);
      setEmail(user.email);
      // Prefill address from the customer's Shopify default address, if they have one
      if (user.gateadresse) setGateadresse(user.gateadresse);
      if (user.postnummer) setPostnummer(user.postnummer);
      if (user.poststed) setPoststed(user.poststed);
    }
  }, [isAuthenticated, user]);
  
  // Handlers for item management
  const handleRemoveItem = (buylistId: number) => {
    dispatch(removeFromBuylist(buylistId));
  };
  
  const handleQuantityChange = (buylistId: number, newQuantity: number) => {
    dispatch(updateBuylistQuantity({ buylistId, quantity: newQuantity }));
  };
  
  // Handle empty cart check with return null pattern
  if (items.length === 0 && !success) {
    // Just return null here too - we'll handle navigation in a button click event
    return <div className="container">
      <PageTitle>Handlekurven din er tom</PageTitle>
      <p>Det er ingen varer i handlekurven.</p>
      <button 
        onClick={() => navigate('/buylist')} 
        style={{
          padding: '0.75rem 1.5rem',
          backgroundColor: theme.colors.primary,
          color: theme.colors.white,
          border: 'none',
          borderRadius: theme.borderRadius.sm,
          cursor: 'pointer',
          marginTop: '1rem'
        }}
      >
        Gå til kjøpsliste
      </button>
    </div>;
  }
  
  const validateContactDetails = (): boolean => {
    const newErrors: ContactErrors = {};

    if (!firstName.trim()) {
      newErrors.firstName = 'Fornavn er påkrevd';
    }
    if (!lastName.trim()) {
      newErrors.lastName = 'Etternavn er påkrevd';
    }

    if (!isAuthenticated) {
      // Guest: all fields required
      if (!email.trim()) {
        newErrors.email = 'E-post er påkrevd';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        newErrors.email = 'Ugyldig e-postformat';
      }
      if (!gateadresse.trim()) {
        newErrors.gateadresse = 'Gateadresse er påkrevd';
      }
      if (!postnummer.trim()) {
        newErrors.postnummer = 'Postnummer er påkrevd';
      }
      if (!poststed.trim()) {
        newErrors.poststed = 'Poststed er påkrevd';
      }
    }

    setContactErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePaymentDetails = (): boolean => {
    // No need to validate for store credit
    if (paymentMethod === BuylistPaymentMethod.STORE_CREDIT) {
      setAccountError(null);
      return true;
    }

    // For KONTO (bank transfer), validate account number format
    if (paymentMethod === BuylistPaymentMethod.KONTO) {
      // Simple validation for Norwegian account number format (11 digits)
      const accountRegex = /^\d{11}$/;
      if (!accountToPayTo || !accountRegex.test(accountToPayTo)) {
        setAccountError('Vennligst oppgi et gyldig 11-sifret kontonummer');
        return false;
      }
    }
    setAccountError(null);
    return true;
  };

  const handleSubmitOrder = async () => {
    if (!paymentMethod) {
      setError('Vennligst velg en betalingsmetode');
      return;
    }

    // Validate contact details
    if (!validateContactDetails()) {
      return;
    }

    // Validate account/phone number if needed
    if (!validatePaymentDetails()) {
      accountNumberRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => accountNumberRef.current?.focus(), 400);
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const orderItems: BuylistOrderItem[] = items.map((cartItem: BuylistCartItem) => ({
        buylistId: cartItem.item.buylistID,
        quantity: cartItem.quantity
      }));

      const orderData: BuylistOrder = {
        items: orderItems,
        totalAmount,
        paymentMethod,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        gateadresse: gateadresse.trim() || undefined,
        postnummer: postnummer.trim() || undefined,
        poststed: poststed.trim() || undefined,
      };

      // Add accountToPayTo for KONTO
      if (paymentMethod === BuylistPaymentMethod.KONTO) {
        orderData.accountToPayTo = accountToPayTo;
      }

      let response;
      if (isAuthenticated) {
        response = await submitBuylistOrder(orderData);
      } else {
        response = await submitGuestBuylistOrder(orderData);
      }

      // Backend kan ha droppet kort hvis buylist-oppføringen ikke fantes lenger
      // (buylisten rekalkuleres jevnlig). Match de droppede ID-ene mot handlekurven
      // for å vise kunden hvilke kort som ikke ble med i ordren.
      const droppedIds: number[] = response.droppedItems || [];
      if (droppedIds.length > 0) {
        const droppedNames = items
          .filter((cartItem: BuylistCartItem) => droppedIds.includes(cartItem.item.buylistID))
          .map((cartItem: BuylistCartItem) =>
            `${cartItem.item.blueprintName}${cartItem.item.foil ? ' (Foil)' : ''} – ${cartItem.item.expansionName}`);
        setDroppedItems(droppedNames);
      }

      setSuccess(true);
      setOrderNumber(response.orderNumber || 'BL-' + Date.now());
      trackPurchase(totalAmount);
      dispatch(clearBuylist());
    } catch (error: any) {
      const errorData = error.response?.data;
      const errorMessage = errorData?.message || errorData?.feil || (typeof errorData === 'string' ? errorData : '');
      if (errorMessage.toLowerCase().includes('verifiser')) {
        setError('Du må verifisere e-postadressen din før du kan legge inn en ordre. Sjekk innboksen din for en verifiserings-e-post.');
      } else {
        setError(errorMessage || 'Det oppstod en feil ved sending av ordren. Vennligst prøv igjen.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (success) {
    return (
      <BuylistLayout>
        <Helmet>
          <title>Kasse | Tryllepapp</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <PageContainer>
          <SuccessMessage>
          <h2>Takk for at du selger til oss!</h2>
          <p>Ordrenummeret ditt er:  <strong>{orderNumber}</strong></p>
            {droppedItems.length > 0 && (
              <DroppedItemsWarning>
                <h3>⚠️ Noen kort ble ikke med i ordren</h3>
                <p>
                  Følgende kort var ikke lenger tilgjengelige på kjøpslisten da ordren ble lagt inn,
                  og ble derfor utelatt. Resten av ordren er registrert som normalt.
                </p>
                <ul>
                  {droppedItems.map((navn, i) => (
                    <li key={i}>{navn}</li>
                  ))}
                </ul>
              </DroppedItemsWarning>
            )}
            <p>Kortene sendes til:</p>
            <p>Tryllepapp AS</p>
            <p>Jonas Lies Vei 61A</p>
            <p>3022 Drammen</p>
            <p>Epost for sporingsinfo: ordre@tryllepapp.no</p>

            <p>Legg ved ordrenummeret.</p>
          <p>Du vil motta en bekreftelsesmail med ytterligere instruksjoner.</p>
          <button 
            style={{ marginTop: '1rem', padding: '0.5rem 1rem', background: theme.colors.primary, color: theme.colors.white, border: 'none', borderRadius: theme.borderRadius.sm, cursor: 'pointer' }}
            onClick={() => navigate('/buylist')}
          >
            Gå til kjøpsliste
          </button>
          </SuccessMessage>
        </PageContainer>
      </BuylistLayout>
    );
  }
  
  return (
    <BuylistLayout>
      <Helmet>
        <title>Kasse | Tryllepapp</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <PageContainer>
        <PageTitle>Kasse - Salgsordre</PageTitle>
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
      
      <CheckoutGrid>
        <div>
          <SectionTitle>Ordregjennomgang</SectionTitle>
          <TableWrapper>
          <OrderTable>
            <TableHead>
              <tr>
                <th>Vare</th>
                <th>Detaljer</th>
                <th>Pris</th>
                <th>Antall</th>
                <th>Totalt</th>
                <th>Handlinger</th>
              </tr>
            </TableHead>
            <TableBody>
              {items.map(({ item, quantity }) => (
                <tr key={item.buylistID}>
                  <td>
                    <ItemImage $imageUrl={getBuylistImageUrl(item.cardBlueprintId, item.image_url)} />
                  </td>
                  <td>
                    <ItemName>{item.blueprintName}</ItemName>
                    <ItemInfo>{item.expansionName}</ItemInfo>
                    <ItemInfo>{item.blueprintVersion}</ItemInfo>
                    {item.foil && <FoilBadge>✨ Foil</FoilBadge>}
                    <ItemInfo>Maks antall: {item.maxQuantity}</ItemInfo>
                  </td>
                  <td>
                    <ItemInfo>{(item.buyPrice || 0).toFixed(2)} kr per stk</ItemInfo>
                    {item.buylistStoreCreditPrice && (
                      <ItemInfo style={{ color: theme.colors.primary }}>
                        {item.buylistStoreCreditPrice.toFixed(2)} kr butikkreditt
                      </ItemInfo>
                    )}
                  </td>
                  <td>
                    <QuantityControl>
                      <QuantityButton 
                        onClick={() => handleQuantityChange(item.buylistID, quantity - 1)}
                        disabled={quantity <= 1 || isSubmitting}
                        title="Reduser antall"
                      >
                        -
                      </QuantityButton>
                      <QuantityDisplay>{quantity}</QuantityDisplay>
                      <QuantityButton
                        onClick={() => handleQuantityChange(item.buylistID, quantity + 1)}
                        disabled={quantity >= item.maxQuantity || isSubmitting}
                        title="Øk antall"
                      >
                        +
                      </QuantityButton>
                    </QuantityControl>
                  </td>
                  <td>
                    <Price>{((item.buyPrice || 0) * quantity).toFixed(2)} kr</Price>
                  </td>
                  <td>
                    <ActionButton 
                      onClick={() => handleRemoveItem(item.buylistID)}
                      disabled={isSubmitting}
                      title="Fjern vare"
                    >
                      ×
                    </ActionButton>
                  </td>
                </tr>
              ))}
            </TableBody>
          </OrderTable>
          </TableWrapper>

          <MobileCardList>
            {items.map(({ item, quantity }) => (
              <MobileCard key={item.buylistID}>
                <MobileCardImage $imageUrl={getBuylistImageUrl(item.cardBlueprintId, item.image_url)} />
                <MobileCardContent>
                  <ItemName>{item.blueprintName}</ItemName>
                  <ItemInfo>{item.expansionName}</ItemInfo>
                  {item.foil && <FoilBadge>✨ Foil</FoilBadge>}
                  <ItemInfo>{(item.buyPrice || 0).toFixed(2)} kr per stk</ItemInfo>
                  <MobileCardRow>
                    <QuantityControl>
                      <QuantityButton
                        onClick={() => handleQuantityChange(item.buylistID, quantity - 1)}
                        disabled={quantity <= 1 || isSubmitting}
                        title="Reduser antall"
                      >
                        -
                      </QuantityButton>
                      <QuantityDisplay>{quantity}</QuantityDisplay>
                      <QuantityButton
                        onClick={() => handleQuantityChange(item.buylistID, quantity + 1)}
                        disabled={quantity >= item.maxQuantity || isSubmitting}
                        title="Øk antall"
                      >
                        +
                      </QuantityButton>
                    </QuantityControl>
                    <Price>{((item.buyPrice || 0) * quantity).toFixed(2)} kr</Price>
                    <ActionButton
                      onClick={() => handleRemoveItem(item.buylistID)}
                      disabled={isSubmitting}
                      title="Fjern vare"
                    >
                      ×
                    </ActionButton>
                  </MobileCardRow>
                </MobileCardContent>
              </MobileCard>
            ))}
          </MobileCardList>

          <ContactSection>
            <SectionTitle>Kontaktinformasjon</SectionTitle>
            <FormRow>
              <div>
                <InputLabel htmlFor="firstName">Fornavn *</InputLabel>
                <InputField
                  id="firstName"
                  type="text"
                  placeholder="Fornavn"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={isSubmitting}
                />
                {contactErrors.firstName && <FieldError>{contactErrors.firstName}</FieldError>}
              </div>
              <div>
                <InputLabel htmlFor="lastName">Etternavn *</InputLabel>
                <InputField
                  id="lastName"
                  type="text"
                  placeholder="Etternavn"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={isSubmitting}
                />
                {contactErrors.lastName && <FieldError>{contactErrors.lastName}</FieldError>}
              </div>
            </FormRow>

            <div>
              <InputLabel htmlFor="email">
                E-post {!isAuthenticated && '*'}
              </InputLabel>
              <InputField
                id="email"
                type="email"
                placeholder="din@epost.no"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting || isAuthenticated}
              />
              {contactErrors.email && <FieldError>{contactErrors.email}</FieldError>}
            </div>

            <div>
              <InputLabel htmlFor="gateadresse">
                Gateadresse {!isAuthenticated && '*'}
              </InputLabel>
              <InputField
                id="gateadresse"
                type="text"
                placeholder="Gateadresse"
                value={gateadresse}
                onChange={(e) => setGateadresse(e.target.value)}
                disabled={isSubmitting}
              />
              {contactErrors.gateadresse && <FieldError>{contactErrors.gateadresse}</FieldError>}
            </div>

            <FormRow>
              <div>
                <InputLabel htmlFor="postnummer">
                  Postnummer {!isAuthenticated && '*'}
                </InputLabel>
                <InputField
                  id="postnummer"
                  type="text"
                  placeholder="0000"
                  value={postnummer}
                  onChange={(e) => setPostnummer(e.target.value)}
                  disabled={isSubmitting}
                  maxLength={4}
                />
                {contactErrors.postnummer && <FieldError>{contactErrors.postnummer}</FieldError>}
              </div>
              <div>
                <InputLabel htmlFor="poststed">
                  Poststed {!isAuthenticated && '*'}
                </InputLabel>
                <InputField
                  id="poststed"
                  type="text"
                  placeholder="Poststed"
                  value={poststed}
                  onChange={(e) => setPoststed(e.target.value)}
                  disabled={isSubmitting}
                />
                {contactErrors.poststed && <FieldError>{contactErrors.poststed}</FieldError>}
              </div>
            </FormRow>
          </ContactSection>

          <PaymentMethodContainer>
            <SectionTitle>Betalingsmetode</SectionTitle>
            {isAuthenticated && (
              <PaymentMethodSelector>
                <PaymentOption className={paymentMethod === BuylistPaymentMethod.KONTO ? 'selected' : ''}>
                  <input
                    type="radio"
                    name="checkoutPaymentMethod"
                    checked={paymentMethod === BuylistPaymentMethod.KONTO}
                    onChange={() => dispatch(setPaymentMethod(BuylistPaymentMethod.KONTO))}
                    disabled={isSubmitting}
                  />
                  Bankoverføring
                </PaymentOption>
                <PaymentOption className={paymentMethod === BuylistPaymentMethod.STORE_CREDIT ? 'selected' : ''}>
                  <input
                    type="radio"
                    name="checkoutPaymentMethod"
                    checked={paymentMethod === BuylistPaymentMethod.STORE_CREDIT}
                    onChange={() => dispatch(setPaymentMethod(BuylistPaymentMethod.STORE_CREDIT))}
                    disabled={isSubmitting}
                  />
                  Butikkreditt (+10% verdi)
                </PaymentOption>
              </PaymentMethodSelector>
            )}
            <PaymentMethodCard>
              {paymentMethod === BuylistPaymentMethod.KONTO ? (
                <>
                  <h3>Bankoverføring</h3>
                  <p>Du vil motta betaling via bankoverføring</p>

                  <WireTransferInfo>
                    <p>Vennligst oppgi kontonummeret du ønsker å motta betaling på.</p>
                    <p>Vi behandler betalingen din innen 2-3 virkedager etter mottak av kortene dine.</p>

                    <InputLabel htmlFor="accountNumber">Kontonummer</InputLabel>
                    <InputField
                      id="accountNumber"
                      ref={accountNumberRef}
                      type="text"
                      placeholder="Oppgi ditt 11-sifret kontonummer"
                      value={accountToPayTo}
                      onChange={(e) => setAccountToPayTo(e.target.value)}
                      disabled={isSubmitting}
                      maxLength={11}
                    />
                    {accountError && <FieldError>{accountError}</FieldError>}
                  </WireTransferInfo>
                </>
               ) : (
                <>
                  <h3>Butikkreditt (+10% mer verdi!)</h3>
                  <p>Du vil motta betaling som butikkreditt</p>
                  
                  <PaymentInfo>
                    <p>Butikkreditt blir lagt til din balanse når vi har gradert og ferdigstilt ordren din, vanligvis innen 2 dager etter mottak.</p>
                  
                    <div style={{ backgroundColor: 'rgba(45, 184, 107, 0.1)', padding: '12px', borderRadius: theme.borderRadius.sm, marginTop: '10px' }}>
                      <p style={{ color: theme.colors.success, margin: 0, fontWeight: 'bold' }}>
                        Din kontantverdi: {totalAmount.toFixed(2)} kr
                      </p>
                      <p style={{ color: theme.colors.success, margin: '5px 0 0 0', fontWeight: 'bold' }}>
                        Din butikkreditt-verdi: {(totalAmount * 1.1).toFixed(2)} kr
                      </p>
                      <p style={{ color: theme.colors.success, margin: '5px 0 0 0' }}>
                        Det er {(totalAmount * 0.1).toFixed(2)} kr ekstra i verdi!
                      </p>
                    </div>
                  </PaymentInfo>
                </>
              )}
            </PaymentMethodCard>
          </PaymentMethodContainer>
        </div>
        
        <OrderSummary>
          <SectionTitle>Ordresammendrag</SectionTitle>
          <SummaryRow>
            <span>Totalt antall:</span>
            <span>{totalQuantity}</span>
          </SummaryRow>
          <SummaryRow>
            <span>Kontantbeløp:</span>
            <span>{totalAmount.toFixed(2)} kr</span>
          </SummaryRow>
          {paymentMethod === BuylistPaymentMethod.STORE_CREDIT && (
            <SummaryRow style={{ backgroundColor: 'rgba(45, 184, 107, 0.1)', padding: '10px', borderRadius: theme.borderRadius.sm, marginTop: '10px' }}>
              <span style={{ fontWeight: 'bold', color: theme.colors.success }}>Butikkreditt-verdi (+10%):</span>
              <span style={{ fontWeight: 'bold', color: theme.colors.success }}>{(totalAmount * 1.1).toFixed(2)} kr</span>
            </SummaryRow>
          )}
          <SummaryRow>
            <span>Betalingsmetode:</span>
            <span>
              {paymentMethod === BuylistPaymentMethod.STORE_CREDIT ? 'Butikkreditt' :
            'Bankoverføring'}
            </span>
          </SummaryRow>
          
          <TermsCheckboxContainer>
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              disabled={isSubmitting}
            />
            <span>
              Ved å bekrefte ordren din godtar du våre{' '}
              <Link to="/terms" target="_blank" rel="noopener noreferrer">vilkår og betingelser</Link>{' '}
              for kjøp av kort.
            </span>
          </TermsCheckboxContainer>

          <ConfirmButton
            onClick={handleSubmitOrder}
            disabled={isSubmitting || !termsAccepted}
          >
            {isSubmitting ? 'Behandler...' : 'Bekreft ordre'}
          </ConfirmButton>
        </OrderSummary>
      </CheckoutGrid>
      </PageContainer>
    </BuylistLayout>
  );
};

export default BuylistCheckoutPage;
